import { isAxiosError } from "axios";
import { useState } from "react";
import type { QueryStatus } from "react-query";
import * as z from "zod";
import { useSectionTiming } from "@/hooks/useSectionTiming";

/** Freshness of the Postgres rows a listing was served from; `synced_at` null and no `failure` means the first sync is still queued. */
export const GithubSnapshot = z
  .object({
    synced_at: z.string().nullable(),
    attempted_at: z.string().nullable(),
    failure: z.string().nullable(),
  })
  .optional();
export type GithubSnapshot = z.infer<typeof GithubSnapshot>;

const SYNC_POLL_MS = 1000;
const SYNC_POLL_WINDOW_MS = 5 * 60 * 1000;

/**
 * `refetchInterval` for a query whose response carries `snapshot`: poll while a sync queued in the last five minutes
 * has neither landed nor failed. A pair nothing ever queued for (no token, nothing to sync) is served as is.
 */
export function pollWhileSyncing<T extends { snapshot?: GithubSnapshot }>(
  data: T | undefined,
): number | false {
  const snapshot = data?.snapshot;
  if (
    !snapshot ||
    snapshot.synced_at !== null ||
    snapshot.failure !== null ||
    snapshot.attempted_at === null
  ) {
    return false;
  }
  return Date.now() - new Date(snapshot.attempted_at).getTime() <
    SYNC_POLL_WINDOW_MS
    ? SYNC_POLL_MS
    : false;
}

/**
 * `useSectionTiming` for a section served from snapshot rows: the wait ends when the rows are usable, i.e. the first
 * response when it was already synced, or the poll that lands the first sync when it was not. A load that started
 * before the first sync reports as `<id>.cold`, so count(cold) / count(all) is the share of cold loads. A load whose
 * first sync has failed or outlasted the poll window reports nothing, like a failed section.
 */
export function useSnapshotSectionTiming(
  id: string,
  status: QueryStatus,
  snapshot: GithubSnapshot | null,
): void {
  const syncing =
    status === "success" &&
    pollWhileSyncing({ snapshot: snapshot ?? undefined }) !== false;
  // A new wait (query key change, refetch after an error) starts warm again.
  const [cold, setCold] = useState(false);
  if (status !== "success" && cold) setCold(false);
  if (syncing && !cold) setCold(true);

  let usable: QueryStatus = status;
  if (status === "success") {
    if (syncing) usable = "loading";
    else if (snapshot?.synced_at === null && snapshot.attempted_at !== null)
      usable = "error";
  }
  useSectionTiming(cold ? `${id}.cold` : id, usable);
}

/** The backend answers 409 while a read waits on a snapshot's first sync, e.g. a user's repository permissions. */
function isSnapshotSyncing(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 409;
}

/** `retry` hook: re-reads a 409 for the window `pollWhileSyncing` polls, so a suspense query stays loading; `undefined` defers to the caller's own policy. */
export function retryWhileSyncing(
  failureCount: number,
  error: unknown,
): boolean | undefined {
  return isSnapshotSyncing(error)
    ? failureCount < SYNC_POLL_WINDOW_MS / SYNC_POLL_MS
    : undefined;
}

/** `retryDelay` paired with `retryWhileSyncing`: one second while syncing, react-query's default backoff otherwise. */
export function syncingRetryDelay(
  failureCount: number,
  error: unknown,
): number {
  return isSnapshotSyncing(error)
    ? SYNC_POLL_MS
    : Math.min(1000 * 2 ** failureCount, 30_000);
}
